import { Button, Flex, HStack, Input, SimpleGrid, Stack, Text, useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import { DateFormControl } from 'BrokerTsLib/src/components'
import type { ModelState } from 'BrokerTsLib/src/dtos'
import { BadRequestError } from 'BrokerTsLib/src/dtos'
import { appendToFormData } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import type { PpzAuthorizationCreate } from '@/dtos/PpzAuthorization/PpzAuthorizationCreate'
import { useCreate } from '@/hooks/usePpzAuthorization'
import { ArrowLeft, Upload } from '@/icons'

import { FormSection } from '../forms/FormSection'
import { PpzAuthorizationFile } from './PpzAuthorizationFile'

const schema = yup.object().shape({
  authorizationValidFrom: yup.date()
    .typeError('Zadejte datum')
    .required('Zadejte datum platnosti od'),
  authorizationValidTo: yup.date()
    .typeError('Zadejte datum')
    .required('Zadejte datum platnosti do')
    .min(yup.ref('authorizationValidFrom'), 'Platnost do musí být po platnosti od')
}) as any

export const PpzAuthorizationCreateForm = ({ userId }: { userId: string }) => {
  const navigate = useNavigate()
  const toast = useToast()
  const form = useForm<PpzAuthorizationCreate>({
    defaultValues: {
      ppzUserId: userId,
      fileNames: []
    },
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    mode: 'all'
  })
  const { handleSubmit, setError, formState: { isSubmitting }, watch, register, setValue, getValues, trigger } = form
  const formRef = useRef<HTMLFormElement>(null)

  const { mutateAsync: createAsync } = useCreate()

  const validFrom = watch('authorizationValidFrom')
  useEffect(() => {
    if (getValues('authorizationValidTo'))
      trigger('authorizationValidTo')
  }, [validFrom])

  const submitHandler = async (data: PpzAuthorizationCreate) => {
    const formData = new FormData(formRef.current!)
    appendToFormData(formData, data)

    await createAsync(formData)
      .then((response) => {
        toast({
          title: 'Autorizace byla úspěšně vytvořena',
          status: 'success'
        })
        navigate(`/ppzAuthorization/${response.id}`)
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 400)
          setModelStateErrors(setError, new BadRequestError(e.response.data as ModelState<PpzAuthorizationCreate>))
        else
          toast({
            title: 'Chyba při vytváření autorizace',
            description: `${e.response?.status}: ${e.response?.statusText}`,
            status: 'error'
          })
      })
  }

  const fileList = watch('files')
  const files = useMemo(() => Array.from(Array(fileList?.length ?? 0).keys()).map(i => fileList[i]), [fileList])
  const fileNames = useMemo(() => files.map(file => file.name), [files])

  useEffect(() => {
    setValue('fileNames', fileNames)
  }, [fileNames])

  const updateFileName = (index: number, newName: string) => {
    const newFileNames = getValues('fileNames')
    newFileNames[index] = newName
    setValue('fileNames', newFileNames)
  }

  const updatedFileNames = watch('fileNames')

  return (
    <FormProvider {...form}>
      <form ref={formRef} onSubmit={handleSubmit(submitHandler)} noValidate>
        <Stack gap={6}>
          <FormSection sectionTitle="Platnost dokladu/osvědčení pojišťovacího zprostředkovatele" wide>
            <Flex gap={4}>
              <DateFormControl isRequired name="authorizationValidFrom" label="Platnost od" flex={1} />
              <DateFormControl isRequired name="authorizationValidTo" label="Platnost do" flex={1} />
            </Flex>
          </FormSection>
          <FormSection sectionTitle={
            <HStack justifyContent='space-between'>
              <Text>Přiložené dokumenty</Text>
              <Button as="label" display='flex' gap={4} justifySelf='start'>
                <Input display='none' type="file" multiple h='auto' {...register('files')} />
                <Upload color="secondary.500" />
                Vybrat soubory
              </Button>
            </HStack>
          } wide>
            {updatedFileNames.length > 0 ?
              <SimpleGrid columns={2} gap={4}>
                {updatedFileNames.map((file, i) => (
                  <PpzAuthorizationFile key={file} name={file} onUpdateName={(name) => updateFileName(i, name)} />
                ))}
              </SimpleGrid>
              :
              <Text fontSize='lg' color='gray.500'>Žádné soubory nebyly vybrány.</Text>
            }
          </FormSection>
          <Flex justifyContent='space-between'>
            <Button onClick={() => navigate(-1)} variant='ghost'>
              <ArrowLeft />
              Zpět
            </Button>
            <Button type="submit" colorScheme="primary" isDisabled={!form.formState.isValid} isLoading={isSubmitting}>Vytvořit</Button>
          </Flex>
        </Stack>
      </form>
    </FormProvider>
  )
}